import { useMemo } from 'react'

import type { ClientUiPublic } from '../../api/platformConfig'
import { comicReferenceCapableModels } from '../../lib/comicCharacterRefs'
import {
  candidatesPerGenerationOptionsForModel,
  isValidComicPanelAspect,
  modelSupportsPanelAspect,
  sizeOptionsForPanelAspect,
  syncComicProjectSettingsToPanelAspect,
  type ComicProjectSettings,
  normalizeComicProjectSettings,
  validateComicProjectSettings,
  validateComicProjectSettingsForPanelAspect,
} from '../../lib/comicProjectSettings'
import { platformDisplayLabel } from '../../lib/platformDisplayLabel'
import { chromeToggleChipClass } from '../ChromeSurface'
import { SelectMenu } from '../SelectMenu'

type Props = {
  angular: boolean
  clientUi: ClientUiPublic
  panelAspect: string
  value: ComicProjectSettings
  onChange: (next: ComicProjectSettings) => void
  /** 仅生图弹窗：平台、模型、每次张数 */
  generationOnly?: boolean
}

export function ComicProjectSettingsForm({
  angular,
  clientUi,
  panelAspect,
  value,
  onChange,
  generationOnly = false,
}: Props) {
  const panelAspectReady = isValidComicPanelAspect(panelAspect)
  const capableModels = useMemo(
    () => comicReferenceCapableModels(clientUi),
    [clientUi],
  )

  const platformOptions = useMemo(() => {
    const platforms = new Map<string, { value: string; label: string }>()
    for (const m of capableModels) {
      if (panelAspectReady && !modelSupportsPanelAspect(clientUi, m.id, m.platform, panelAspect)) {
        continue
      }
      if (!platforms.has(m.platform)) {
        platforms.set(m.platform, {
          value: m.platform,
          label: platformDisplayLabel(m.platform),
        })
      }
    }
    return Array.from(platforms.values())
  }, [capableModels, clientUi, panelAspect, panelAspectReady])

  const modelMenuOptions = useMemo(
    () =>
      capableModels
        .filter((m) => m.platform === value.platformId)
        .filter((m) => !panelAspectReady || modelSupportsPanelAspect(clientUi, m.id, m.platform, panelAspect))
        .map((m) => ({ value: m.id, label: m.label, description: m.description })),
    [capableModels, clientUi, panelAspect, panelAspectReady, value.platformId],
  )

  const candidatesOptions = useMemo(
    () => candidatesPerGenerationOptionsForModel(clientUi, value.modelId, value.platformId),
    [clientUi, value.modelId, value.platformId],
  )

  const sizeMenuOptions = useMemo(() => {
    return sizeOptionsForPanelAspect(clientUi, value.modelId, value.platformId, panelAspect).map((o) => ({
      value: o.value,
      label: o.label,
      description: o.description,
    }))
  }, [clientUi, panelAspect, value.modelId, value.platformId])

  const validation = useMemo(
    () =>
      panelAspectReady
        ? validateComicProjectSettingsForPanelAspect(value, clientUi, panelAspect)
        : validateComicProjectSettings(value, clientUi),
    [value, clientUi, panelAspect, panelAspectReady],
  )

  const patch = (partial: Partial<ComicProjectSettings>) => {
    const normalized = normalizeComicProjectSettings({ ...value, ...partial }, clientUi)
    onChange(
      panelAspectReady
        ? syncComicProjectSettingsToPanelAspect(normalized, clientUi, panelAspect)
        : normalized,
    )
  }

  const handlePlatformChange = (platformId: string) => {
    const first = capableModels.find(
      (m) =>
        m.platform === platformId &&
        (!panelAspectReady || modelSupportsPanelAspect(clientUi, m.id, m.platform, panelAspect)),
    )
    patch({ platformId, modelId: first?.id ?? '', apiSize: '' })
  }

  if (generationOnly) {
    if (capableModels.length === 0) {
      return (
        <p className="text-sm text-ink-soft">
          当前没有支持参考图的平台或模型，请在后台启用对应生图平台后重试
        </p>
      )
    }
    if (!panelAspectReady) {
      return null
    }
    return (
      <div className="space-y-5">
        {!validation.ok ? (
          <p className="text-sm text-[color-mix(in_srgb,var(--ink)_75%,var(--color-warning)_25%)]" role="alert">
            {validation.message}
          </p>
        ) : null}
        {platformOptions.length > 1 ? (
          <section>
            <label className="mb-1.5 block text-xs font-semibold text-ink">平台</label>
            <SelectMenu
              id="comic-settings-platform"
              ariaLabel="AI 平台"
              value={value.platformId}
              onChange={handlePlatformChange}
              options={platformOptions}
              angular={angular}
            />
          </section>
        ) : null}

        <section className="min-w-0">
          <label className="mb-1.5 block text-xs font-semibold text-ink">模型</label>
          {modelMenuOptions.length > 0 ? (
            <SelectMenu
              id="comic-settings-model"
              ariaLabel="图像模型"
              value={value.modelId}
              onChange={(modelId) => patch({ modelId })}
              options={modelMenuOptions}
              angular={angular}
            />
          ) : (
            <p className="text-sm text-ink-soft">该平台下暂无支持当前单页比例的模型</p>
          )}
        </section>

        {sizeMenuOptions.length > 0 ? (
          <section className="min-w-0">
            <label className="mb-1.5 block text-xs font-semibold text-ink">尺寸</label>
            <SelectMenu
              id="comic-settings-size"
              ariaLabel="输出尺寸"
              value={value.apiSize}
              onChange={(apiSize) => patch({ apiSize })}
              options={sizeMenuOptions}
              angular={angular}
            />
          </section>
        ) : (
          <p className="text-sm text-ink-soft">当前模型没有与画册比例对齐的明确尺寸</p>
        )}

        <section>
          <p className="text-xs font-semibold text-ink">每次生成本页</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {candidatesOptions.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => patch({ candidatesPerGeneration: n })}
                className={chromeToggleChipClass(angular, value.candidatesPerGeneration === n)}
              >
                {n} 张
              </button>
            ))}
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-ink-soft">完整话级参数请在编辑页「生图设置」中调整。</p>
    </div>
  )
}
